import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSearchParams } from 'react-router-dom';
import { MdCleaningServices } from 'react-icons/md';
import { GoCalendar } from 'react-icons/go';
import { BsPersonFill } from 'react-icons/bs';
import { ImUserCheck } from 'react-icons/im';
import { BiSortAZ } from 'react-icons/bi';
import ReactTooltip from 'react-tooltip';
import { DateTime } from 'luxon';
import { RootState } from '../../../../../infrastructure/redux/store';
import { IGradeAlocacao, limparTurmas, ordenarTurmas } from '../../reducers/alocacao.reducer';
import { useValidarAlocacaoLazyQuery } from '../../graphql/queries/validar_alocacao.query';
import { validar_alocacao } from '../../utils/validators/alocacao.validator';
import { Scroll } from '../../../shared/components/layout_components/scroll';
import { sigla } from '../../../shared/utils/formatters/sigla.formatter';
import { ColunaAcoes, Acao } from './coluna_acoes';
import { AlocacoesCalendario } from './alocacoes_calendario';
import { Container, Grade, GradeBody, GradeHeader, GradeTitulo, HeaderButton } from './styles';

interface Props {
  grades: IGradeAlocacao[];
}

const GradeAlocacoes: React.FC<Props> = (props) => {
  const { theme } = useSelector((state: RootState) => state.theme);
  const { selecao } = useSelector((state: RootState) => state.alocacao);

  const dispatch = useDispatch();

  const [obter_alocacao, { data: alocacao_cache, refetch: refetch_obter_alocacao }] =
    useValidarAlocacaoLazyQuery();

  const abrir_grade_horaria = (id_oferta: number, id_turma: number, id_periodo: number) => {
    const params = createSearchParams({
      id_oferta: String(id_oferta),
      id_turma: String(id_turma),
      id_periodo: String(id_periodo),
    });

    window.open(`/alocacao/grade?${params}`, '_blank');
  };

  const validar_alocacoes = async () => {
    if (!selecao.professor || !selecao.disciplina || !selecao.horario) return;

    const mensagens = new Array<string>();

    for (const selec_alocacao of selecao.alocacoes) {
      if (selec_alocacao.alocacao.professor) {
        continue;
      }

      const { data } = alocacao_cache
        ? await refetch_obter_alocacao({
            id_professor: selecao.professor.id_professor,
            id_horario: selecao.horario.id_horario,
            dat_alocacao: selec_alocacao.alocacao.dat_alocacao,
          })
        : await obter_alocacao({
            variables: {
              id_professor: selecao.professor.id_professor,
              id_horario: selecao.horario.id_horario,
              dat_alocacao: selec_alocacao.alocacao.dat_alocacao,
            },
          });

      const data_formatada = DateTime.fromISO(selec_alocacao.alocacao.dat_alocacao, {
        setZone: true,
      }).toFormat('dd/MM/yyyy');

      if (data?.validar_alocacoes_professores.length) {
        for (const alocacao of data.validar_alocacoes_professores) {
          mensagens.push(
            `Já existe alocação na grade ${sigla(alocacao.oferta.curso?.dsc_nome_curso, alocacao.oferta.curso?.dsc_sigla_curso)} ${
              alocacao.oferta.num_oferta
            }.${alocacao.turma.num_turma} para a disciplina ${
              alocacao.disciplina.dsc_sigla
            } - ${data_formatada}`
          );
        }
      }
    }

    if (mensagens.length) {
      alert(mensagens.join('\n'));
    } else {
      alert('Alocações válidas!');
    }
  };

  const coluna_acoes: Acao[] = [
    {
      label: 'Limpar',
      icone: MdCleaningServices,
      visivel: Boolean(props.grades.length),
      cor: theme.colors.primary,
      cor_texto: theme.colors.textPrimary,
      onClick: () => dispatch(limparTurmas()),
    },
    {
      label: 'Ordenar',
      icone: BiSortAZ,
      visivel: Boolean(props.grades.length),
      cor: theme.colors.primary,
      cor_texto: theme.colors.textPrimary,
      onClick: () => dispatch(ordenarTurmas()),
    },
    {
      label: 'Exibir todas alocações',
      icone: BsPersonFill,
      visivel: Boolean(selecao.professor),
      cor: theme.colors.primary,
      cor_texto: theme.colors.textPrimary,
      onClick: () => dispatch(ordenarTurmas()),
    },
    {
      label: 'Validar alocação',
      icone: ImUserCheck,
      visivel: validar_alocacao(selecao),
      cor: theme.colors.primary,
      cor_texto: theme.colors.textPrimary,
      onClick: validar_alocacoes,
    },
  ];

  return (
    <Container>
      <ColunaAcoes acoes={coluna_acoes} />
      <Scroll scroll-auto>
        {props.grades.map((grade) => (
          <Grade key={grade.id_grade}>
            <GradeHeader>
              <GradeTitulo>{grade.dsc_nome_grade}</GradeTitulo>
              <HeaderButton
                onClick={() =>
                  abrir_grade_horaria(grade.id_oferta, grade.id_turma, grade.id_periodo)
                }
                data-tip
                data-for={grade.id_grade}
              >
                <GoCalendar color={theme.colors.textPrimary} size={20} />
                <ReactTooltip id={grade.id_grade} effect='solid'>
                  <span>Abrir grade horária</span>
                </ReactTooltip>
              </HeaderButton>
            </GradeHeader>
            <GradeBody>
              <AlocacoesCalendario data={grade} />
            </GradeBody>
          </Grade>
        ))}
      </Scroll>
    </Container>
  );
};

export { GradeAlocacoes };
